import { useState, useEffect } from "react";
import { Heart, Menu, X, Globe } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

const Navbar = () => {
  const { t, lang, toggleLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "mission", label: t.nav.mission },
    { id: "impact", label: t.nav.impact },
    { id: "donate", label: t.nav.donate },
    { id: "transparency", label: t.nav.transparency },
    { id: "contact", label: t.nav.contact },
  ];

  const handleNav = (id) => {
    setMobileOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 200);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FDFBF7]/95 backdrop-blur-md border-b border-black/5 shadow-sm"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="container-narrow flex items-center justify-between h-20">
        <button
          onClick={() => {
            setMobileOpen(false);
            if (!isHome) {
              navigate("/");
            } else {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          className="flex items-center gap-2.5 group"
          data-testid="logo-button"
        >
          <div className="w-9 h-9 rounded-full bg-[#002B49] flex items-center justify-center transition-transform group-hover:scale-105">
            <Heart className="w-4.5 h-4.5 text-[#C5A059]" fill="#C5A059" size={18} />
          </div>
          <div className="text-left leading-tight">
            <div className={`font-serif text-lg font-semibold ${scrolled ? "text-[#002B49]" : "text-white"}`}>
              Cœur Solidaire
            </div>
            <div className={`text-[10px] uppercase tracking-[0.18em] ${scrolled ? "text-[#64748B]" : "text-white/70"}`}>
              Fondation
            </div>
          </div>
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => handleNav(l.id)}
              className={`text-sm font-medium transition-colors ${
                scrolled ? "text-[#334155] hover:text-[#002B49]" : "text-white/85 hover:text-white"
              }`}
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`hidden md:flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${
              scrolled
                ? "text-[#334155] hover:bg-black/5"
                : "text-white/90 hover:bg-white/10"
            }`}
            data-testid="lang-toggle"
            aria-label="Switch language"
          >
            <Globe size={16} />
            {lang.toUpperCase()}
          </button>

          <button
            onClick={() => handleNav("donate")}
            className="hidden md:inline-flex btn-primary py-2.5 px-5 text-sm"
            data-testid="navbar-donate-btn"
          >
            {t.nav.donateBtn}
          </button>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-md ${scrolled ? "text-[#002B49]" : "text-white"}`}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-black/5 shadow-lg" data-testid="mobile-menu">
          <div className="container-narrow py-4 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => handleNav(l.id)}
                className="text-left py-3 px-2 text-[#334155] hover:bg-[#F3F0EA] rounded-md font-medium"
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-3 py-3 border-t border-black/5 mt-2">
              <button
                onClick={toggleLang}
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md hover:bg-black/5"
                data-testid="mobile-lang-toggle"
              >
                <Globe size={16} />
                {lang.toUpperCase()}
              </button>
              <button
                onClick={() => handleNav("donate")}
                className="btn-primary py-2.5 px-5 text-sm flex-1 justify-center"
                data-testid="mobile-donate-btn"
              >
                {t.nav.donateBtn}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
